import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/hen-ui/utils';
import {
  SPLIT_RATIO_TOKENS,
  splitItemStickyVariants,
  splitVariants,
  type SplitRatio,
  type SplitVariantProps,
} from './split.variants';

export type { SplitRatio };

/** DSL JSON and shortcode attributes are untyped, so an off-list value uses equal. */
function resolveSplitRatio(ratio: string): SplitRatio {
  return (SPLIT_RATIO_TOKENS as readonly string[]).includes(ratio)
    ? (ratio as SplitRatio)
    : 'equal';
}

export interface SplitProps
  extends React.HTMLAttributes<HTMLDivElement>,
    Omit<SplitVariantProps, 'reverse' | 'ratio'> {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
  /** Column width ratio */
  ratio?: SplitRatio;
  /** Reverse order on desktop */
  reverse?: boolean;
  /** Direct column content. Use SplitItem as direct children of Split. */
  children?: React.ReactNode;
}

export type SplitItemSpaceAbove = 'none' | 'header' | 'sm';

export interface SplitItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
  /** Keep this column on screen while the other column scrolls. */
  sticky?: boolean;
  /** Room between the stuck column and the top of the screen. */
  spaceAbove?: SplitItemSpaceAbove;
}

/**
 * Split - Responsive two-column layout component
 *
 * Replaces manual `<div class="flex flex-col md:flex-row gap-*">` patterns.
 * Use Split for side-by-side content that stacks on mobile.
 *
 * @example Basic usage
 * ```tsx
 * <Split gap="3xl" ratio="equal">
 *   <SplitItem>
 *     <ImageCard ... />
 *   </SplitItem>
 *   <SplitItem>
 *     <ImageCard ... />
 *   </SplitItem>
 * </Split>
 * ```
 *
 * @example With ratio
 * ```tsx
 * <Split gap="xl" ratio="1/3-2/3">
 *   <SplitItem>
 *     <Sidebar />
 *   </SplitItem>
 *   <SplitItem>
 *     <MainContent />
 *   </SplitItem>
 * </Split>
 * ```
 *
 * @example Reversed order on desktop
 * ```tsx
 * <Split gap="2xl" reverse>
 *   <SplitItem>Content on right (desktop)</SplitItem>
 *   <SplitItem>Content on left (desktop)</SplitItem>
 * </Split>
 * ```
 *
 * @example Fractional ratio
 * ```tsx
 * <Split gap="xl" ratio="4/9-5/9">
 *   <SplitItem>
 *     <SellerInfo />
 *   </SplitItem>
 *   <SplitItem>
 *     <ContactForm />
 *   </SplitItem>
 * </Split>
 * ```
 */
export const Split = React.forwardRef<HTMLDivElement, SplitProps>(
  (
    {
      className,
      gap = 'xl',
      breakpoint = 'md',
      ratio: ratioProp = 'equal',
      reverse = false,
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div';
    const ratio = resolveSplitRatio(ratioProp);

    return (
      <Comp
        className={cn(
          splitVariants({ gap, breakpoint, ratio, reverse }),
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

Split.displayName = 'Split';

/**
 * SplitItem - Child component for Split layout
 *
 * Use SplitItem as direct children of Split to define the two columns.
 * A sticky item stays on screen until the end of the Split.
 */
export const SplitItem = React.forwardRef<HTMLDivElement, SplitItemProps>(
  (
    {
      className,
      asChild = false,
      sticky = false,
      spaceAbove = 'none',
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp
        className={cn(
          sticky && splitItemStickyVariants({ spaceAbove }),
          className,
        )}
        data-hen-split-sticky={sticky ? '' : undefined}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

SplitItem.displayName = 'SplitItem';
